import { BackLink } from "@/components/layout-2/ds";
import { dialogClass, type DialogSize } from "@/components/layout-2/dialog-size";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * A detail of the Feeding tab (the preview, the passes, one pass, the history) in a large dialog over the
 * tab, reached from a sub-route of it: closing goes back to the tab. `back` leads to the parent detail
 * (a pass → all passes) in place, one dialog at a time.
 */
export function DetailDialog({
    title,
    badge,
    meta,
    actions,
    back,
    footer,
    size = "xl",
    onClose,
    testId,
    children
}: {
    title: React.ReactNode;
    badge?: React.ReactNode;
    meta?: React.ReactNode;
    actions?: React.ReactNode;
    back?: { label: string; to: string };
    footer?: React.ReactNode;
    size?: DialogSize;
    onClose: () => void;
    testId?: string;
    children: React.ReactNode;
}) {
    return (
        <Dialog open onOpenChange={open => !open && onClose()}>
            <DialogContent className={dialogClass(size, "h-[90dvh] p-0 gap-0")} data-testid={testId}>
                <DialogHeader className="flex-shrink-0 gap-1 border-b px-6 pb-4 pt-5 pr-12">
                    {back && <BackLink label={back.label} to={back.to} />}
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <DialogTitle>{title}</DialogTitle>
                                {badge}
                            </div>
                            {meta ? <DialogDescription>{meta}</DialogDescription> : <DialogDescription className="sr-only">Detail of the feeding</DialogDescription>}
                        </div>
                        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
                    </div>
                </DialogHeader>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
                {footer && <DialogFooter className="flex-shrink-0 items-center gap-2 border-t px-6 py-3 sm:justify-between sm:gap-0">{footer}</DialogFooter>}
            </DialogContent>
        </Dialog>
    );
}
